import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  User,
  Mail,
  Phone,
  Building2,
  CreditCard,
  MapPin,
  UserCircle,
  Briefcase,
} from "lucide-react";

export function ClientCard(props) {
  const {
    name,
    email,
    phoneNumber,
    country,
    billingName,
    bankName,
    IBAN,
    lucaId,
    lucaAccountName,
    currency,
    newTvAccountManager,
    isHaveAgency,
    editClick,
    isHorizontal,
  } = props;

  return (
    <Card
      className={cn(
        "group hover:shadow-xl transition-all duration-300 hover:border-primary/50 overflow-hidden h-full flex flex-col",
        isHorizontal && "flex-row justify-between items-center"
      )}
    >
      {/* Header with gradient accent */}
      <div className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex-shrink-0" />

      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl mb-1 group-hover:text-primary transition-colors truncate">
              {name || "-"}
            </CardTitle>
            <CardDescription className="flex items-center gap-1.5 text-sm">
              <Mail className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{email || "-"}</span>
            </CardDescription>
          </div>
          {country && (
            <Badge variant="secondary" className="flex-shrink-0">
              <MapPin className="h-3 w-3 mr-1" />
              {country}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4 flex-grow">
        {/* Contact & Business Info Grid - Always show 2 rows */}
        <div className="grid grid-cols-2 gap-3 text-sm min-h-[50px]">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="truncate">{phoneNumber || "-"}</span>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground">
            <CreditCard className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="font-medium">{currency || "-"}</span>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground">
            <UserCircle className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="truncate">{newTvAccountManager || "-"}</span>
          </div>

          {isHaveAgency && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-xs">Has Agency</span>
            </div>
          )}
        </div>

        {/* Luca Info - Always show section with placeholder if empty */}
        <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800 min-h-[70px]">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">
              Luca Account
            </span>
          </div>
          <p className="text-sm font-medium text-foreground mb-0.5 truncate">
            {lucaAccountName || "-"}
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            ID: {lucaId || "-"}
          </p>
        </div>

        {/* Bank Info - Always show section with placeholder if empty */}
        <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800 min-h-[70px]">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">
              Banking Details
            </span>
          </div>
          <p className="text-sm font-medium text-foreground mb-0.5 truncate">
            {bankName || "-"}
          </p>
          <p className="text-xs text-muted-foreground font-mono truncate">
            {IBAN || "-"}
          </p>
        </div>

        {/* Billing Name */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-2 rounded bg-muted/30">
          <User className="h-4 w-4 flex-shrink-0" />
          <span className="text-xs">Billing:</span>
          <span className="font-medium truncate">{billingName || name || "-"}</span>
        </div>
      </CardContent>

      <CardFooter className="pt-4 border-t bg-muted/20 flex-shrink-0 mt-auto">
        <Button
          onClick={editClick}
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
          variant="outline"
        >
          View Details →
        </Button>
      </CardFooter>
    </Card>
  );
}
